// Account Section - User account management page
// Based on the main section structure with account-specific content

import { AccountManager } from './managers/account-manager';
import { MainLayout } from '../../components/Layout';
import { logger, useManager } from '../../core';
import { useAuthRequired, useAuth } from '../../contexts/AuthContext';
import { useNavigationManager } from '../../managers/navigation-manager';
import { RevoltSegmentedControl } from '../../components';
import {
  ProfileCard,
  MembershipCard,
  ModelsCard,
  QuickLinksCard,
  SettingsCard,
  DiscordCard,
  TransferSection,
  GumroadBundleSection,
  LegalSection
} from './components';
import './AccountSection.css';
import { useEffect, useState } from 'react';

const ACCOUNT_TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'settings', label: 'Settings' },
  { value: 'models', label: 'Models' },
  { value: 'links', label: 'Links' },
  { value: 'discord', label: 'Discord' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'gumroad', label: 'Gumroad' },
  { value: 'legal', label: 'Legal' },
];

export function AccountSection() {
  const { manager: accountManager, isInitialized } = useManager(AccountManager);
  const navigationManager = useNavigationManager();
  const [activeTab, setActiveTab] = useState('overview');
  const [accountUsage, setAccountUsage] = useState(null);

  // Use authentication payload system - automatically redirects to auth if needed
  const { user } = useAuthRequired({
    payload: {
      action: 'viewAccount',
      section: 'account',
      timestamp: Date.now()
    }
  });

  // Get refreshUser and updateStoredAccountsForUser from useAuth hook
  const { refreshUser, updateStoredAccountsForUser, membershipDetails } = useAuth();

  // Guard: keep activeTab valid (must be before any early returns)
  useEffect(() => {
    if (!ACCOUNT_TABS.find((t) => t.value === activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab]);

  // Load plan-bar usage in one API call (/api/account/usage)
  useEffect(() => {
    if (!isInitialized) return;
    if (!user?.id) return;

    let cancelled = false;

    (async () => {
      try {
        const usage = await accountManager.fetchAccountUsage();
        if (!cancelled) setAccountUsage(usage);
      } catch (error) {
        // Non-blocking: keep the account page usable even if usage fails to load.
        if (!cancelled) setAccountUsage(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountManager, isInitialized, user?.id]);

  // Don't render until manager is initialized
  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--account">
        <div className="loading-container">
          <div>Loading your account...</div>
        </div>
      </MainLayout>
    );
  }

  logger.log({
    tags: "section|account|render",
    color1: "lime"
  }, "AccountSection rendering");

  const handleUpgrade = () => {
    // Navigate to pricing with payload indicating upgrade action
    navigationManager.navigateWithPayload('pricing', {
      action: 'upgrade_plan',
      fromSection: 'account',
      currentPlan: user.membership?.planId || 'free',
      timestamp: Date.now()
    });
  };

  const handleManageSubscription = () => {
    // Open Stripe customer portal
    accountManager.handleManageSubscription();
  };

  const handleCreditUpgrade = () => {
    // Navigate to pricing with payload indicating credit upgrade
    navigationManager.navigateWithPayload('pricing', {
      action: 'upgrade_credits',
      fromSection: 'account',
      currentPlan: user.membership?.planId || 'free',
      timestamp: Date.now()
    });
  };

  const handleNavigateToSection = (section) => {
    accountManager.navigateToSection(section);
  };

  const handleSaveProfile = async (profilePayload) => {
    try {
      const response = await fetch('/api/account/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profilePayload),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        // Update the local user state to reflect the changes
        // This will trigger a re-render with the new initials
        if (profilePayload.initials !== undefined || profilePayload.username !== undefined) {
          // Refresh user data from server without full page reload
          refreshUser();

          // Update the stored account data so the account switcher shows updated info
          updateStoredAccountsForUser({
            initials: profilePayload.initials,
            username: profilePayload.username
          });
        }

        // Success - no alert needed, state update provides feedback
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      alert('Failed to update profile: ' + error.message);
    }
  };

  return (
    <MainLayout contentClassName="main-content--account">
      <div className="account-container">
        <div className="account-content">
          <h1 className="account-title">My Account</h1>

          <div className="account-tabs">
            <RevoltSegmentedControl
              options={ACCOUNT_TABS}
              value={activeTab}
              onChange={setActiveTab}
              size="default"
              equalWidthPadding={4}
            />
          </div>

          <div className="account-tab-content">
            {activeTab === 'overview' && (
              <div className="account-grid">
                <div className="account-row equal-height">
                  <div className="account-panel is-wide">
                    <ProfileCard user={user} onSaveProfile={handleSaveProfile} />
                  </div>
                  <div className="account-panel">
                    <MembershipCard
                      user={user}
                      membershipDetails={membershipDetails}
                      usage={accountUsage}
                      subscriptionStatus={null} // TODO: Implement subscription status
                      onUpgrade={handleUpgrade}
                      onManageSubscription={handleManageSubscription}
                      onCreditUpgrade={handleCreditUpgrade}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <SettingsCard user={user} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'models' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <ModelsCard user={user} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'links' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <QuickLinksCard onNavigateToSection={handleNavigateToSection} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'discord' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <DiscordCard user={user} viewMode="auto" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transfer' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <TransferSection user={user} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'gumroad' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <GumroadBundleSection
                      user={user}
                      onNavigateToTransfer={() => setActiveTab('transfer')}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'legal' && (
              <div className="account-grid">
                <div className="account-row">
                  <div className="account-panel is-wide">
                    <LegalSection />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
